import { SocialLinks } from 'social-links';
import { linkify } from './stringUtils.js';
import axios from 'axios';

const scrubLink = (text) => {
  const links = linkify.find(text);
  if (links.length > 0 && links[0].href) {
    return links[0].href;
  }
  return null;
};

const socialLinks = new SocialLinks({
  usePredefinedProfiles: true,
  trimInput: true,
  allowQueryParams: false,
});

const sanitizeAccounts = ({
  youtubeUrl,
  webUrl,
  facebookName,
  instagramName,
  twitterName,
}) => ({
  youtubeUrl: youtubeUrl
    ? socialLinks.sanitize('youtube', youtubeUrl.replace('/c/', '/channel/'))
    : null,
  twitterName: twitterName
    ? socialLinks.getProfileId(
        'twitter',
        twitterName.includes('x.com')
          ? twitterName.replace(/x\.com/g, 'twitter.com')
          : twitterName,
      )
    : null,
  facebookName: facebookName
    ? socialLinks.getProfileId('facebook', facebookName)
    : null,
  instagramName: instagramName
    ? socialLinks.getProfileId('instagram', instagramName)
    : null,
  webUrl: webUrl ? scrubLink(webUrl) : null,
});

const sanitizeLink = (url, socialType) => {
  if (!url || !socialType) return null;

  if (socialType === 'website') {
    return scrubLink(url);
  }

  if (socialType === 'youtube') {
    return socialLinks.sanitize('youtube', url.replace('/c/', '/channel/'));
  }

  // Normalize x.com URLs to twitter.com and handle 'x' socialType
  const normalizedSocialType = socialType === 'x' ? 'twitter' : socialType;
  const normalizedUrl =
    normalizedSocialType === 'twitter' && url.includes('x.com')
      ? url.replace(/x\.com/g, 'twitter.com')
      : url;

  return socialLinks.sanitize(normalizedSocialType, normalizedUrl);
};

const validateLink = (url, socialType) => {
  if (!url) return true;
  if (!socialType) return false;

  if (socialType === 'website') {
    return Boolean(scrubLink(url));
  }

  if (socialType === 'youtube') {
    return socialLinks.isValid('youtube', url.replace('/c/', '/channel/'));
  }

  // Normalize x.com URLs to twitter.com and handle 'x' socialType
  const normalizedSocialType = socialType === 'x' ? 'twitter' : socialType;
  const normalizedUrl =
    normalizedSocialType === 'twitter' && url.includes('x.com')
      ? url.replace(/x\.com/g, 'twitter.com')
      : url;

  return socialLinks.isValid(normalizedSocialType, normalizedUrl);
};

const validateAccounts = ({
  youtubeUrl,
  webUrl,
  facebookName,
  instagramName,
  twitterName,
}) => ({
  ...(webUrl ? { webUrl: !!scrubLink(webUrl) } : {}),
  ...(youtubeUrl
    ? {
        youtubeUrl: socialLinks.isValid(
          'youtube',
          youtubeUrl.replace('/c/', '/channel/'),
        ),
      }
    : { youtubeUrl: true }),
  ...(twitterName
    ? {
        twitterName: socialLinks.isValid(
          'twitter',
          twitterName.includes('x.com')
            ? twitterName.replace(/x\.com/g, 'twitter.com')
            : twitterName,
        ),
      }
    : { twitterName: true }),
  ...(facebookName
    ? {
        facebookName: socialLinks.isValid('facebook', facebookName),
      }
    : { facebookName: true }),
  ...(instagramName
    ? {
        instagramName: socialLinks.isValid('instagram', instagramName),
      }
    : { instagramName: true }),
});

const emailValidate = (value) => {
  const isValid =
    value !== '' &&
    !(value && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value));
  return isValid;
};

const zipValidate = (value) => {
  const isValid = !(value && !/^[0-9]{5}(?:-[0-9]{4})?$/i.test(value));
  return isValid;
};

const checkoutFormValidate = ({
  firstName,
  lastName,
  email,
  charityId,
  charityIds,
  onNoCharitySelected,
}) => {
  const validationObj = {
    firstName: true,
    lastName: true,
    email: true,
    isValid: true,
  };
  if (!firstName) {
    validationObj.firstName = false;
    validationObj.isValid = false;
  }
  if (!lastName) {
    validationObj.lastName = false;
    validationObj.isValid = false;
  }
  if (!email || !emailValidate(email)) {
    validationObj.email = false;
    validationObj.isValid = false;
  }
  if (charityIds.length === 0 && !charityId) {
    validationObj.isValid = false;
    onNoCharitySelected();
  }

  return validationObj;
};

const userCheckoutFormValidate = ({ firstName, lastName, email, card }) => {
  const validationObj = {
    firstName: true,
    lastName: true,
    email: true,
    isValid: true,
  };
  if (!firstName) {
    validationObj.firstName = false;
    validationObj.isValid = false;
  }
  if (!lastName) {
    validationObj.lastName = false;
    validationObj.isValid = false;
  }
  if (!email || !emailValidate(email)) {
    validationObj.email = false;
    validationObj.isValid = false;
  }
  if (card && !card._complete) {
    validationObj.card = false;
    validationObj.isValid = false;
  }

  return validationObj;
};

const signupValidate = ({ firstName, lastName, email, password }) => {
  const validationObj = {
    firstName: true,
    lastName: true,
    email: true,
    password: true,
    isValid: true,
  };
  if (!firstName) {
    validationObj.firstName = false;
    validationObj.isValid = false;
  }
  if (!lastName) {
    validationObj.lastName = false;
    validationObj.isValid = false;
  }
  if (!email || !emailValidate(email)) {
    validationObj.email = false;
    validationObj.isValid = false;
  }

  if (!password || password.length < 8) {
    validationObj.password = false;
    validationObj.isValid = false;
  }

  return validationObj;
};

const getYoutubeVideoId = (url) => {
  if (!url) return null;

  // Handle youtu.be URLs
  if (url.includes('youtu.be/')) {
    return url.split('youtu.be/')[1].split('?')[0];
  }

  // Handle youtube.com URLs
  if (url.includes('youtube.com')) {
    const urlObj = new URL(url);
    // Handle watch?v= format
    if (urlObj.pathname === '/watch') {
      return urlObj.searchParams.get('v');
    }
    // Handle embed/ and v/ formats
    const pathParts = urlObj.pathname.split('/');
    if (pathParts[1] === 'embed' || pathParts[1] === 'v') {
      return pathParts[2];
    }
  }

  return null;
};

const getYoutubeThumbnail = (videoId, quality = 'maxres') => {
  if (!videoId) return null;

  const qualities = {
    default: 'default.jpg',
    medium: 'mqdefault.jpg',
    high: 'hqdefault.jpg',
    standard: 'sddefault.jpg',
    maxres: 'maxresdefault.jpg',
  };

  const qualityKey = qualities[quality] || qualities.default;
  return `https://img.youtube.com/vi/${videoId}/${qualityKey}`;
};

const getVimeoVideoId = (url) => {
  if (!url) return null;

  if (url.includes('vimeo.com/')) {
    return url.split('vimeo.com/')[1].split('?')[0];
  }

  return null;
};

const getVimeoThumbnail = async (videoId) => {
  if (!videoId) return null;

  return axios
    .get(`https://vimeo.com/api/oembed.json?url=https://vimeo.com/${videoId}`)
    .then((res) => {
      return res.data.thumbnail_url || null;
    })
    .catch((err) => {
      console.error(err);
      return null;
    });
};

const getUrlFromString = (text) => {
  if (!text || typeof text !== 'string') return null;

  const links = linkify.find(text);
  if (links.length > 0 && links[0].href) {
    return links[0].href;
  }

  return null;
};

export {
  scrubLink,
  sanitizeAccounts,
  validateAccounts,
  emailValidate,
  zipValidate,
  checkoutFormValidate,
  userCheckoutFormValidate,
  signupValidate,
  sanitizeLink,
  validateLink,
  getYoutubeVideoId,
  getYoutubeThumbnail,
  getUrlFromString,
  getVimeoVideoId,
  getVimeoThumbnail,
};
